import React, { createContext, useContext, useState, useEffect } from "react";
import api, { refreshTokensFromStorage } from "../api/apiClient";

const AuthContext = createContext(null);

// ASA-180 fase 3b: de sessie leunt op de httpOnly-cookie; tokens staan niet meer in
// localStorage. We ruimen eventuele oude localStorage-resten op (migratie).
function clearLegacyStorage() {
  localStorage.removeItem("ventus_token");
  localStorage.removeItem("ventus_refresh_token");
  localStorage.removeItem("ventus_user");
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function hydrate() {
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) {
          setUser(data);
          clearLegacyStorage();
        }
        return;
      } catch (e) {
        if (e?.response?.status === 401) {
          const ok = await refreshTokensFromStorage();
          if (ok && !cancelled) {
            try {
              const { data } = await api.get("/auth/me");
              if (!cancelled) {
                setUser(data);
                clearLegacyStorage();
              }
              return;
            } catch {
              /* val door naar uitgelogd */
            }
          }
        }
      }
      if (!cancelled) {
        setUser(null);
        clearLegacyStorage();
      }
    }
    hydrate().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onRefreshed = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        /* ignore */
      }
    };
    const onExpired = () => setUser(null);
    window.addEventListener("ventus-auth-refreshed", onRefreshed);
    window.addEventListener("ventus-auth-expired", onExpired);
    return () => {
      window.removeEventListener("ventus-auth-refreshed", onRefreshed);
      window.removeEventListener("ventus-auth-expired", onExpired);
    };
  }, []);

  const login = (authResponse) => {
    // Token zit in de httpOnly-cookie (door de backend gezet); niet meer in localStorage.
    clearLegacyStorage();
    setUser({
      userId: authResponse.userId,
      email: authResponse.email,
      fullName: authResponse.fullName,
      role: authResponse.role,
    });
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* cookie wordt server-side gewist; negeer fouten */
    }
    clearLegacyStorage();
    setUser(null);
  };

  const isAdmin = user?.role === "ADMIN";

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
